import React, { useState, useEffect } from 'react';
import { Download, Trash2, Edit, Shield, AlertCircle, CheckCircle, Clock } from 'lucide-react';
import { createPrivacyRequest, getPrivacyRequests, exportUserData, requestAccountDeletion, cancelAccountDeletion } from '../../services/privacyService';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';


/**
 * Privacy Center Page
 * GDPR/CCPA self-service portal
 */
const PrivacyCenter = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showDeletionModal, setShowDeletionModal] = useState(false);
  const [deletionConfirmText, setDeletionConfirmText] = useState('');
  
  useEffect(() => {
    loadRequests();
  }, []);
  
  const loadRequests = async () => {
    setLoading(true);
    const result = await getPrivacyRequests();
    if (result?.success) {
      setRequests(result?.data || []);
    }
    setLoading(false);
  };
  
  const handleExportData = async (format) => {
    const result = await exportUserData(format);
    if (result?.success) {
      alert(`Your data has been exported in ${format?.toUpperCase()} format.`);
    } else {
      alert(`Export failed: ${result?.error}`);
    }
  };
  
  const handleRequestDeletion = async () => {
    if (deletionConfirmText !== 'DELETE MY ACCOUNT') {
      alert('Please type "DELETE MY ACCOUNT" to confirm.');
      return;
    }
    
    const result = await requestAccountDeletion();
    if (result?.success) {
      alert('Account deletion request submitted. You have 30 days to cancel this request.');
      setShowDeletionModal(false);
      setDeletionConfirmText('');
      loadRequests();
    } else {
      alert(`Request failed: ${result?.error}`);
    }
  };
  
  const handleCancelDeletion = async (requestId) => {
    if (!confirm('Are you sure you want to cancel your account deletion request?')) return;
    
    const result = await cancelAccountDeletion(requestId);
    if (result?.success) {
      alert('Account deletion request cancelled.');
      loadRequests();
    } else {
      alert(`Cancellation failed: ${result?.error}`);
    }
  };
  
  const handleRectifyData = async () => {
    const result = await createPrivacyRequest({
      requestType: 'rectification',
      rectificationData: { note: 'User requested data rectification' }
    });
    
    if (result?.success) {
      alert('Data rectification request submitted. Our team will contact you within 30 days.');
      loadRequests();
    } else {
      alert(`Request failed: ${result?.error}`);
    }
  };
  
  const getStatusBadge = (status) => {
    const badges = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'Pending' },
      in_progress: { color: 'bg-blue-100 text-blue-800', icon: Clock, label: 'In Progress' },
      completed: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Completed' },
      rejected: { color: 'bg-red-100 text-red-800', icon: AlertCircle, label: 'Rejected' }
    };
    
    const badge = badges?.[status] || badges?.pending;
    const Icon = badge?.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${badge?.color}`}>
        <Icon className="w-4 h-4" />
        {badge?.label}
      </span>
    );
  };
  
  const getRequestTypeLabel = (type) => {
    const labels = {
      access: 'Data Access',
      erasure: 'Account Deletion',
      rectification: 'Data Rectification',
      portability: 'Data Portability',
      objection: 'Processing Objection'
    };
    return labels?.[type] || type;
  };
  
  const pendingDeletionRequest = requests?.find(r => r?.request_type === 'erasure' && r?.request_status === 'pending');
  
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Privacy Center</h1>
          <p className="text-gray-600">Manage your data and privacy preferences in compliance with GDPR and CCPA</p>
        </div>
        
        {/* Disclaimer */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <h3 className="font-semibold text-blue-900 mb-1">GDPR/CCPA-Ready Privacy Tools</h3>
              <p className="text-sm text-blue-800">
                FireCaseIQ provides GDPR and CCPA-ready privacy tools. Legal compliance should be verified with legal counsel.
                All requests are processed within 30 days as required by law.
              </p>
            </div>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              {[
                { id: 'overview', label: 'Overview' },
                { id: 'requests', label: 'My Requests' },
                { id: 'consents', label: 'Consent Management' }
              ]?.map(tab => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab?.id
                      ? 'border-primary text-primary' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab?.label}
                </button>
              ))}
            </nav>
          </div>
        </div>
        
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Right to Access */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Download className="w-6 h-6 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Right to Access</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Download all your personal data in machine-readable format (JSON or CSV).
                  </p>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleExportData('json')}
                      variant="outline"
                      size="sm"
                    >
                      Download JSON
                    </Button>
                    <Button
                      onClick={() => handleExportData('csv')}
                      variant="outline"
                      size="sm"
                    >
                      Download CSV
                    </Button>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Right to Erasure */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-red-100 rounded-lg">
                  <Trash2 className="w-6 h-6 text-red-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Right to Erasure</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Request permanent deletion of your account and personal data. 30-day grace period applies.
                  </p>
                  {pendingDeletionRequest ? (
                    <div>
                      <p className="text-sm text-orange-600 font-medium mb-2">
                        Deletion scheduled for {new Date(pendingDeletionRequest?.scheduled_erasure_date)?.toLocaleDateString()}
                      </p>
                      <Button
                        onClick={() => handleCancelDeletion(pendingDeletionRequest?.id)}
                        variant="outline"
                        size="sm"
                      >
                        Cancel Deletion
                      </Button>
                    </div>
                  ) : (
                    <Button
                      onClick={() => setShowDeletionModal(true)}
                      variant="danger"
                      size="sm"
                    >
                      Request Deletion
                    </Button>
                  )}
                </div>
              </div>
            </div>
            
            {/* Right to Rectification */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-green-100 rounded-lg">
                  <Edit className="w-6 h-6 text-green-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Right to Rectification</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Request correction of inaccurate or incomplete personal data.
                  </p>
                  <Button
                    onClick={handleRectifyData}
                    variant="outline"
                    size="sm"
                  >
                    Request Correction
                  </Button>
                </div>
              </div>
            </div>
            
            {/* Right to Data Portability */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <Download className="w-6 h-6 text-purple-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Right to Data Portability</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Export your data in a structured, machine-readable format for transfer to another service.
                  </p>
                  <Button
                    onClick={() => handleExportData('json')}
                    variant="outline"
                    size="sm"
                  >
                    Export Data
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Requests Tab */}
        {activeTab === 'requests' && (
          <div className="bg-white rounded-lg shadow-sm">
            {loading ? (
              <div className="p-8 text-center text-gray-500">Loading requests...</div>
            ) : requests?.length === 0 ? (
              <div className="p-8 text-center text-gray-500">No privacy requests found</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Request Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Deadline</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {requests?.map(request => (
                      <tr key={request?.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {getRequestTypeLabel(request?.request_type)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(request?.request_status)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(request?.created_at)?.toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(request?.deadline_date)?.toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {request?.request_type === 'erasure' && request?.request_status === 'pending' && (
                            <button
                              onClick={() => handleCancelDeletion(request?.id)}
                              className="text-red-600 hover:text-red-800 font-medium"
                            >
                              Cancel
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
        
        {/* Consents Tab */}
        {activeTab === 'consents' && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <p className="text-gray-600 mb-4">Manage your data processing consents. Essential cookies cannot be disabled.</p>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                <div>
                  <h4 className="font-medium text-gray-900">Essential Cookies</h4>
                  <p className="text-sm text-gray-600">Required for basic site functionality</p>
                </div>
                <div className="text-sm text-gray-500">Always Active</div>
              </div>
              
              <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                <div>
                  <h4 className="font-medium text-gray-900">Analytics Cookies</h4>
                  <p className="text-sm text-gray-600">Help us improve our service</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" className="sr-only peer" />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>
              
              <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                <div>
                  <h4 className="font-medium text-gray-900">Marketing Cookies</h4>
                  <p className="text-sm text-gray-600">Used for targeted advertising</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" className="sr-only peer" />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Deletion Confirmation Modal */}
      {showDeletionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="p-3 bg-red-100 rounded-lg">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Confirm Account Deletion</h3>
                <p className="text-sm text-gray-600 mb-4">
                  This action will permanently delete your account and all associated data after a 30-day grace period.
                  You can cancel this request within 30 days.
                </p>
              </div>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Type "DELETE MY ACCOUNT" to confirm:
              </label>
              <input
                type="text"
                value={deletionConfirmText}
                onChange={(e) => setDeletionConfirmText(e?.target?.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="DELETE MY ACCOUNT"
              />
            </div>
            
            <div className="flex gap-3">
              <Button
                onClick={() => {
                  setShowDeletionModal(false);
                  setDeletionConfirmText('');
                }}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleRequestDeletion}
                variant="danger"
                className="flex-1"
                disabled={deletionConfirmText !== 'DELETE MY ACCOUNT'}
              >
                Confirm Deletion
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrivacyCenter;